/* MolCellQuest — Firebase との同期（トランスポート層）
   ──────────────────────────────────────────────────────────
   アプリ本体（src/）はこのファイルに依存しない。両者は
   window.MCQSync / window.MCQBridge という2つのグローバルだけで
   つながっている。だから次のどの場合でも本体は動き続ける：
     1. firebase-config.js が未設定（"YOUR_..." のまま）
     2. Firebase SDK の初期化に失敗した
     3. file:// で開いていて、このモジュール自体が読めない
   ────────────────────────────────────────────────────────── */

const SDK = "https://www.gstatic.com/firebasejs/10.14.1";

/** アプリ側のブリッジが用意されるまで待つ */
function waitBridge(cb) {
  const b = window.MCQBridge;
  if (b) cb(b);
  else setTimeout(() => waitBridge(cb), 120);
}

/** 状態の読み込みが済むまで待つ（進捗の上書き事故を防ぐ） */
function waitReady() {
  return new Promise((resolve) => {
    (function check() {
      const b = window.MCQBridge;
      if (b && b.ready) resolve(b);
      else setTimeout(check, 120);
    })();
  });
}

function disable(reason, error) {
  window.MCQSync = { enabled: false, reason, error: error ? String(error) : undefined };
  waitBridge((b) => b.onSyncState && b.onSyncState({ enabled: false, reason }));
}

(async function init() {
  const cfg = window.FIREBASE_CONFIG;
  const unconfigured = !cfg || !cfg.apiKey || String(cfg.apiKey).startsWith("YOUR_");
  if (unconfigured) {
    disable("unconfigured");
    return;
  }

  let initializeApp, getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword;
  let fbSignOut, onAuthStateChanged, getFirestore, doc, setDoc, getDoc, collection, query;
  let orderBy, limit, onSnapshot, serverTimestamp;

  try {
    ({ initializeApp } = await import(`${SDK}/firebase-app.js`));
    ({
      getAuth,
      createUserWithEmailAndPassword,
      signInWithEmailAndPassword,
      signOut: fbSignOut,
      onAuthStateChanged,
    } = await import(`${SDK}/firebase-auth.js`));
    ({
      getFirestore,
      doc,
      setDoc,
      getDoc,
      collection,
      query,
      orderBy,
      limit,
      onSnapshot,
      serverTimestamp,
    } = await import(`${SDK}/firebase-firestore.js`));
  } catch (e) {
    disable("sdk-load-error", e);
    return;
  }

  let app, auth, db;
  try {
    app = initializeApp(cfg);
    auth = getAuth(app);
    db = getFirestore(app);
  } catch (e) {
    disable("init-error", e);
    return;
  }

  let currentUser = null;
  let pushTimer = null;
  let unsubLeaderboard = null;
  // 次の push に「最終ログイン日時」を含めるかどうか。
  // ログイン直後に単独で書き込もうとすると、セキュリティルールが要求する
  // evo を含められない（ドキュメント未作成のとき）ため、push に相乗りさせる。
  let stampLogin = false;

  /* クラウドへの書き込みを許可してよいか。
     ──────────────────────────────────────────────────────
     ログイン直後は必ず「先に pull、あとで push」でなければならない。
     ローカルがまだ空のまま push が走ると、setDoc(merge:true) は
     ネストしたマップもフィールド単位で合成するため、full.evo のような
     スカラーが 0 で上書きされ、クラウドの記録が消える。
     初回 pull が確実に終わるまで、あらゆる push を止める。      */
  let pullDone = false;
  let pullRetry = null;
  // 最後に把握しているクラウド側の進化ポイント。
  // evo は単調増加なので、これを下回る値を書こうとしていたら何かが壊れている。
  let remoteEvo = 0;

  /** 解答のたびに書き込まないよう、4秒ぶんをまとめる */
  function schedulePush() {
    if (!currentUser || !pullDone) return;
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(push, 4000);
  }

  function cancelPush() {
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = null;
  }

  async function push() {
    pushTimer = null;
    if (!currentUser || !window.MCQBridge) return;
    if (!pullDone) return; // 取得前には絶対に書かない

    const p = window.MCQBridge.getProgress();
    const evo = Math.max(0, Number(p.evo) || 0);

    // 安全弁：evo は減らないはずなので、クラウドより小さい値を書こうと
    // しているならマージが効いていない。書かずに取得からやり直す。
    if (evo + 1e-9 < remoteEvo) {
      console.error(
        `[MolCellQuest] 進捗が後退しています（ローカル ${evo} < クラウド ${remoteEvo}）。書き込みを中止して取り直します。`
      );
      pullDone = false;
      initialPull(currentUser.uid);
      return;
    }

    try {
      // 公開ドキュメント（ランキング表示に必要な分だけ）
      const publicDoc = {
        nickname: p.nickname || "",
        bio: (p.bio || "").slice(0, 160),
        evo,
        eraId: p.eraId,
        form: p.form,
        organism: p.organism || null,
        solvedCount: p.solvedCount || 0,
        updatedAt: serverTimestamp(),
      };
      if (stampLogin) {
        publicDoc.lastLoginAt = serverTimestamp();
        stampLogin = false;
      }
      await setDoc(doc(db, "users", currentUser.uid), publicDoc, { merge: true });
      remoteEvo = evo;
      // 本人だけが読み書きする詳細データ
      await setDoc(
        doc(db, "userdata", currentUser.uid),
        { full: p.full, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      // 失敗しても次の機会に再送されるので、ここでは黙って諦める
      console.warn("[MolCellQuest] 同期に失敗しました", e);
    }
  }

  /**
   * クラウドの進捗をローカルへマージする。
   * @returns {Promise<boolean>} 取得できたか（ドキュメント未作成も成功とみなす）
   */
  async function pull(uid) {
    try {
      const snap = await getDoc(doc(db, "userdata", uid));
      if (snap.exists()) {
        const data = snap.data();
        if (data && data.full && window.MCQBridge) {
          remoteEvo = Math.max(0, Number(data.full.evo) || 0);
          window.MCQBridge.applyRemoteProgress(data.full);
        }
      }
      return true;
    } catch (e) {
      // ここで諦めて push を許すと記録を消してしまう。失敗は失敗として返す。
      console.warn("[MolCellQuest] 進捗の取得に失敗しました", e);
      return false;
    }
  }

  /**
   * 初回の取得。成功するまで書き込みを解禁しない。
   * 通信が不安定なときのために、間隔を空けて数回だけ再試行する。
   */
  async function initialPull(uid, attempt = 0) {
    if (!currentUser || currentUser.uid !== uid) return; // その間にログアウト／別人になった
    const ok = await pull(uid);
    if (!currentUser || currentUser.uid !== uid) return;

    if (ok) {
      pullDone = true;
      push();
      return;
    }
    if (attempt < 5) {
      const wait = Math.min(30000, 3000 * 2 ** attempt);
      console.warn(`[MolCellQuest] 進捗の取得を ${wait / 1000} 秒後に再試行します`);
      pullRetry = setTimeout(() => initialPull(uid, attempt + 1), wait);
    } else {
      // 取得できないまま書き込むと記録を失うので、同期はあきらめる。
      // ローカルの学習記録はそのまま残り、次回の起動でまた取得を試みる。
      console.error("[MolCellQuest] クラウドの進捗を取得できませんでした。この画面では同期を行いません。");
    }
  }

  function watchLeaderboard() {
    if (unsubLeaderboard) unsubLeaderboard();
    const q = query(collection(db, "users"), orderBy("evo", "desc"), limit(50));
    unsubLeaderboard = onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ uid: d.id, ...d.data() }));
        waitBridge((b) => b.onLeaderboard && b.onLeaderboard(rows));
      },
      (e) => {
        console.warn("[MolCellQuest] ランキングの購読に失敗しました", e);
        waitBridge((b) => b.onLeaderboard && b.onLeaderboard([]));
      }
    );
  }

  // ランキングはログインしていなくても見られる（読み取りはルールで公開）。
  // 認証状態に関係なく、起動時に一度だけ購読を張る。
  watchLeaderboard();

  onAuthStateChanged(auth, async (user) => {
    const wasSignedOut = !currentUser;
    currentUser = user;

    // 認証状態が変わったら、前のユーザー向けに予約されていた書き込みを必ず捨てる。
    // 取得が終わるまで書き込みは再び禁止。
    cancelPush();
    if (pullRetry) clearTimeout(pullRetry);
    pullRetry = null;
    pullDone = false;
    remoteEvo = 0; // 別のアカウントの値を持ち越さない

    waitBridge((b) => b.onAuth && b.onAuth(user ? { uid: user.uid, email: user.email } : null));

    if (user) {
      if (wasSignedOut) stampLogin = true; // 次の push で最終ログイン日時を記録する
      await waitReady(); // ローカルの読み込み完了を待つ
      await initialPull(user.uid); // 取得 → マージ → ここで初めて書き込みを解禁
    }
    // サインアウトしてもランキングの購読は続ける（見るだけなら誰でもできる）
  });

  function friendlyError(e) {
    const code = (e && e.code) || "";
    if (code.includes("email-already-in-use")) return "このメールアドレスは登録済みです。";
    if (code.includes("invalid-email")) return "メールアドレスの形式が正しくありません。";
    if (code.includes("weak-password")) return "パスワードは6文字以上にしてください。";
    if (code.includes("invalid-credential") || code.includes("wrong-password"))
      return "メールアドレスかパスワードが違います。";
    if (code.includes("too-many-requests")) return "試行回数が多すぎます。しばらく待ってください。";
    if (code.includes("network")) return "ネットワークに接続できませんでした。";
    return "うまくいきませんでした。もう一度お試しください。";
  }

  window.MCQSync = {
    enabled: true,
    reason: "ok",

    markDirty: schedulePush,

    async signUp(nickname, email, password) {
      try {
        await createUserWithEmailAndPassword(auth, email, password);
        if (window.MCQBridge) {
          const p = window.MCQBridge.getProgress();
          if (!p.nickname && nickname) {
            // ニックネームは state 側で持っているので、次の push で反映される
          }
        }
        schedulePush();
        return { ok: true };
      } catch (e) {
        return { ok: false, error: friendlyError(e) };
      }
    },

    async signIn(email, password) {
      try {
        await signInWithEmailAndPassword(auth, email, password);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: friendlyError(e) };
      }
    },

    async signOut() {
      if (pushTimer) {
        clearTimeout(pushTimer);
        await push();
      }
      await fbSignOut(auth);
    },
  };

  waitBridge((b) => b.onSyncState && b.onSyncState({ enabled: true, reason: "ok" }));
})();
